/**
 * Menu open/close, placement, and the actions that dismiss it.
 */

import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import useOnTaskIdentity from '../hooks/useOnTaskIdentity';

const MENU_HEIGHT = 420;
const MENU_WIDTH = 180;

function clampMenuTop(top) {
  const viewportHeight = window.innerHeight;
  if (top + MENU_HEIGHT > viewportHeight) {
    return viewportHeight - MENU_HEIGHT - 10;
  }
  return top;
}

export function useDragMenuState(ref, { task, onAddIcon }) {
  const [showMenu, setShowMenu] = useState(false);
  const [explodeModalOpen, setExplodeModalOpen] = useState(false);
  const [milestoneChoiceOpen, setMilestoneChoiceOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const handleRef = useRef(null);
  const menuRef = useRef(null);

  useOnTaskIdentity(task?.id, () => {
    setShowMenu(false);
    setExplodeModalOpen(false);
    setMilestoneChoiceOpen(false);
  });

  useImperativeHandle(ref, () => ({
    openAtPosition: (x, y) => {
      let left = x;
      if (left + MENU_WIDTH > window.innerWidth) {
        left = window.innerWidth - MENU_WIDTH - 10;
      }
      setMenuPosition({ top: clampMenuTop(y), left });
      setShowMenu(true);
    },
    closeMenu: () => setShowMenu(false),
  }), []);

  useEffect(() => {
    const name = showMenu ? 'taskDragMenuOpened' : 'taskDragMenuClosed';
    window.dispatchEvent(new CustomEvent(name, {
      detail: { taskId: task.id },
    }));
  }, [showMenu, task.id]);

  useEffect(() => {
    if (!showMenu) return undefined;

    const handleClickOutside = (e) => {
      if (handleRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu) return undefined;

    const handlePanStart = () => setShowMenu(false);
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [showMenu]);

  const handleAction = (action) => (e) => {
    e.stopPropagation();
    setShowMenu(false);
    action?.();
  };

  const handleAddIcon = (e) => {
    e.stopPropagation();
    const position = { x: menuPosition.left + MENU_WIDTH, y: menuPosition.top };
    setShowMenu(false);
    onAddIcon?.(null, position);
  };

  return {
    showMenu,
    setShowMenu,
    menuPosition,
    setMenuPosition,
    explodeModalOpen,
    setExplodeModalOpen,
    milestoneChoiceOpen,
    setMilestoneChoiceOpen,
    handleRef,
    menuRef,
    handleAction,
    handleAddIcon,
  };
}
